<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue';
import type { PageName } from '@/api/tracking';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { useSurveyAccordion } from '@/components/survey/composables/useSurveyHistoryCommon';
import SurveyContentRenderer from '@/components/survey/generate-content/survey-content-renderer.vue';
import SurveyFeedback from '@/components/survey/survey-feedback.vue';
import type {
  AdpDocument,
  FeedbackType,
  Part,
  SurveyHistoryStatus,
  UserDocument,
} from '@/types';
import { parseModelResponse } from '@/utils/survey/common';
import { SURVEY_FEATURE_NAME } from '@/utils/survey/constants';

interface Props {
  sessionId: string;
  requestId: string;
  question: string;
  sources: Array<AdpDocument | UserDocument>;
  originMarkdownReasoning: string;
  reasoning: Array<Part>;
  originMarkdownSummary: string;
  summary: Array<Part>;
  enableAnimation: boolean;
  enableFeedback: boolean;
  feedbackType: FeedbackType | undefined;
  status: SurveyHistoryStatus;
  pageName: PageName;
}

const props = defineProps<Props>();

// スレッドを開いた初期表示では、並列にパース処理を行うことでパフォーマンスを向上させる
const computedSummary = computed(() => {
  return parseModelResponse(
    props.originMarkdownSummary,
    props.sources,
    false,
    false,
  );
});
const computedReasoning = computed(() => {
  return parseModelResponse(
    props.originMarkdownReasoning,
    props.sources,
    true,
    false,
  );
});

// 各途中経過のメッセージを最低でも一定時間表示する
const MIN_PROGRESS_DISPLAY_MS = 2500;
const displayedProgressMessage = ref('');
// Promise チェーンをメッセージキューとして使用
let pendingMessages = Promise.resolve();
const delay = (ms: number) =>
  new Promise<void>(resolve => setTimeout(resolve, ms));

// 新しい途中経過メッセージが来た場合、前のメッセージの表示が終わるまで待ってから表示する
watch(
  () => props.status.progressMessage,
  (newMsg, oldMsg) => {
    // immediate 初回かつ初期値が undefined の場合をスキップ
    if (newMsg === oldMsg) return;
    pendingMessages = pendingMessages.then(async () => {
      displayedProgressMessage.value = newMsg;
      if (newMsg !== '') await delay(MIN_PROGRESS_DISPLAY_MS);
    });
  },
  { immediate: true },
);

// 推論（CoT）もしくは途中経過メッセージのセクション全体を表示するかどうか
const isProgressReasoningVisible = computed(() => {
  return (
    !props.status.isNoHit &&
    (displayedProgressMessage.value !== '' ||
      props.reasoning.length > 0 ||
      computedReasoning.value.length > 0)
  );
});

// ローディング中の表示をするかどうか
const isLoadingProgress = computed(
  () =>
    props.status.isInProgress &&
    displayedProgressMessage.value !== '' &&
    props.reasoning.length === 0 &&
    props.summary.length === 0,
);

// アコーディオンの状態管理
const {
  isSummaryVisible,
  isAccordionOpen,
  accordionRef,
  isAutoToggledAccordion,
  toggleAccordion,
} = useSurveyAccordion({
  enableAnimation: toRef(props, 'enableAnimation'),
  reasoning: toRef(props, 'reasoning'),
  summary: toRef(props, 'summary'),
});

const showFeedbackButton = computed(() => {
  return (
    props.requestId &&
    !props.status.isInProgress &&
    !props.status.isError &&
    props.enableFeedback
  );
});
</script>

<template>
  <div class="answer-content">
    <div
      v-if="isProgressReasoningVisible"
      class="survey-history-content c-text-regular--m"
    >
      <div class="progress-reasoning-wrapper">
        <!-- 要約 途中経過メッセージ -->
        <template v-if="isLoadingProgress">
          <div class="in-progress-message">
            <Transition name="fade-text" mode="out-in">
              <span :key="displayedProgressMessage">
                {{ displayedProgressMessage }}
              </span>
            </Transition>
          </div>
        </template>
        <!-- Chain-of-thoughtの結果 -->
        <template
          v-else-if="computedReasoning.length > 0 || props.reasoning.length > 0"
        >
          <div class="accordion-toggle" @click="toggleAccordion">
            <div class="accordion-icon-and-text">
              <span>回答生成のための推論結果</span>
            </div>
            <DgrIcon
              name="chevron-down"
              size="small"
              class="toggle-icon"
              :class="{ 'is-open': isAccordionOpen }"
            />
          </div>
          <div
            ref="accordionRef"
            class="accordion-content-wrapper"
            :class="{ 'is-open': isAccordionOpen }"
            style="
              --survey-heading-font-size: 14px;
              --survey-text-font-size: 14px;
            "
          >
            <div class="accordion-content">
              <SurveyContentRenderer
                :content-parts="
                  props.enableAnimation ? props.reasoning : computedReasoning
                "
                :enable-animation="
                  props.enableAnimation ? !isAutoToggledAccordion : false
                "
                :status="props.status"
                feature="survey"
                :page-name="props.pageName"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
    <div class="survey-history-content">
      <SurveyContentRenderer
        v-if="
          (computedSummary.length > 0 || props.summary.length > 0) &&
          isSummaryVisible
        "
        v-memo="[props.summary, props.enableAnimation, props.status]"
        :content-parts="props.enableAnimation ? props.summary : computedSummary"
        :enable-animation="props.enableAnimation"
        :status="props.status"
        :contents-context="{
          event_name: SURVEY_FEATURE_NAME,
          session_id: props.sessionId,
        }"
        feature="survey"
        :page-name="props.pageName"
      />
      <template v-else-if="props.status.isNoHit">
        <p>
          「{{ props.question }}」
          に関するソースを見つけられなかったため要約を生成できませんでした。
        </p>
        <p>
          質問をより明確にするか、要約に使用する情報源を変更することで要約を作成できる可能性があります。
        </p>
        <div class="spacing-08"></div>
      </template>
      <div class="summary-feedback">
        <SurveyFeedback
          v-if="showFeedbackButton"
          :session-id="props.sessionId"
          :request-id="props.requestId"
          :feedback-type="props.feedbackType"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.answer-content {
  margin: 16px 0 8px;
}

.survey-history-content {
  padding: 0 16px;
}

.progress-reasoning-wrapper {
  font-size: 14px;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  margin-bottom: 16px;
}

.in-progress-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  min-height: 24px;
}

.accordion-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 16px;
  cursor: pointer;
  user-select: none;
}

.accordion-icon-and-text {
  display: flex;
  align-items: center;
  gap: 8px;
  align-items: flex-start;
}

.toggle-icon {
  transition: transform 0.3s;
  &.is-open {
    transform: rotate(180deg);
  }
}

.accordion-content-wrapper {
  display: block;
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.3s ease;
  transform-origin: top;
}
.accordion-content-wrapper.is-open {
  max-height: 2480px;
}

.accordion-content {
  padding: 4px 16px 16px;
}

// Transitionを使用したアニメーション
.fade-text-enter-active,
.fade-text-leave-active {
  transition: all 0.3s ease;
}
.fade-text-enter-from,
.fade-text-leave-to {
  opacity: 0;
}
.fade-text-enter-to,
.fade-text-leave-from {
  opacity: 1;
}

.summary-feedback {
  background-color: $color-gray200;
  border-radius: 4px;
}
</style>
